import { useFormatter } from '@api/useFormatter'
import { useOrders } from '@api/useOrders'
import { UpdateHistoryDrawer } from '@components/_SiteWide/AuditHistory/UpdateHistoryDrawer'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { DateRangeFilter } from '@/components/_SiteWide/DateRangeFilter'
import { GraviGrid, Horizontal } from '@gravitate-js/excalibrr'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { useUserSettings } from '@hooks/settings'
import { getCurrentMarket } from '@utils/general'
import type { AgGridReactProps } from 'ag-grid-react'
import moment from 'moment'
import React, { useMemo, useState } from 'react'

import { useAuditReportColumnDefs } from './columnDefs'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'order-audit-report'

export function OrderAuditReportPage() {
  const filterStorageKey = 'order-audit-day-ranges'
  const { formatCurrency } = useFormatter()
  const { updateSetting, getSetting } = useUserSettings()

  const market = getCurrentMarket()
  const { useAuditReportQuery } = useOrders()

  const { value: orderAuditDayRanges, setValue: setOrderAuditDayRanges } = useLocalStorage('order-audit-day-ranges', {
    back: -3,
    ahead: 0,
  })

  const [orderAuditPayload, setOrderAuditPayload] = useState({
    start_date: moment()
      .add(orderAuditDayRanges?.back ?? -3, 'days')
      .startOf('day')
      .toISOString(),
    end_date: moment()
      .add(orderAuditDayRanges?.ahead ?? 0, 'days')
      .endOf('day')
      .toISOString(),
  })

  const { data, isPending, isFetching } = useAuditReportQuery(orderAuditPayload, market)

  const agPropOverrides = useMemo(() => {
    return {
      enableCharts: true,
      getRowId: (row) => row.data.order_id,
      enableRangeSelection: true,
      rowSelection: 'multiple',
      tooltipShowDelay: 0,
      rowHeight: 30,
      groupDisplayType: 'multipleColumns',
      suppressAggFuncInHeader: true,
      getContextMenuItems: (params) => {
        const contextMenuItems = [
          'copy',
          'copyWithHeaders',
          'paste',
          'separator',
          'export',
          'separator',
          'chartRange',
          'pivotChartAndPivotMode',
          'separator',
          'resetColumns',
          'expandAll',
          'contractAll',
        ]

        return contextMenuItems
      },
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalAndFilteredRowCountComponent' },
          { statusPanel: 'agTotalRowCountComponent' },
          { statusPanel: 'agFilteredRowCountComponent' },
          { statusPanel: 'agSelectedRowCountComponent' },
          { statusPanel: 'agAggregationComponent' },
        ],
      },
    } as AgGridReactProps
  }, [])

  const [selectedOrderId, setSelectedOrderId] = useState(false)

  const columnDefs = useAuditReportColumnDefs(formatCurrency, setSelectedOrderId)

  function settingUpdateWrapper(payload) {
    if (typeof payload === 'function') return
    if (payload.start_date && payload.end_date) {
      setOrderAuditPayload(payload)
    } else {
      setOrderAuditDayRanges(payload)
      updateSetting(filterStorageKey, payload)
      setOrderAuditPayload({
        start_date: moment().add(payload.back, 'days').startOf('day').toISOString(),
        end_date: moment().add(payload.ahead, 'days').endOf('day').toISOString(),
      })
    }
  }

  const excelDownloadOptions = useMemo(
    () => ({
      sheetName: 'Order Audits',
      author: 'Gravitate',
      fileName: 'Audit Report.xlsx',
      // skipColumnGroupHeaders: true,
      allColumns: true,
    }),
    []
  )
  const gridAPIRef = React.useRef(null)

  return (
    <>
      <UpdateHistoryDrawer closeAuditDrawer={() => setSelectedOrderId(null)} selectedOrderId={selectedOrderId} />
      <GraviGrid
        externalRef={gridAPIRef}
        storageKey={STORAGE_KEY}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        controlBarProps={{
          title: 'Order Audit Report',
          actionButtons: (
            <Horizontal gap={16} alignItems='center'>
              <UploadDownloadButtons
                excelOptions={[
                  {
                    title: 'Export All Columns (Default)',
                    onClick: () => gridAPIRef.current.exportDataAsExcel(excelDownloadOptions),
                  },
                  {
                    title: 'Export Only Visible Columns',
                    onClick: () => gridAPIRef.current.exportDataAsExcel({ ...excelDownloadOptions, allColumns: false }),
                  },
                ]}
              />
              <DateRangeFilter
                currentFilters={orderAuditPayload}
                setCurrentFilters={settingUpdateWrapper}
                fromKey='start_date'
                toKey='end_date'
                getFromStorage={() => orderAuditDayRanges}
                min={-90}
                max={0}
                maxDate={new Date()}
                dayRangeStorage={orderAuditDayRanges}
                setDayRangeStorage={settingUpdateWrapper}
              />
            </Horizontal>
          ),
        }}
        agPropOverrides={agPropOverrides}
        rowData={data}
        columnDefs={columnDefs}
        loading={isPending || isFetching}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
    </>
  )
}
